// src/professions.ts
export type ProfessionKey = 'doctor' | 'education' | 'tech' | 'property' | 'financial' | 'allied' | 'migration' | 'legal'

type ProfessionConfig = {
  label: string
  listSpecialtiesPath: string // backend path key your api client understands
  searchLabel: string
  resultsLabel: string
  ctaLabel: string
  tzNote?: string
}

export const PROFESSIONS: Record<ProfessionKey, ProfessionConfig> = {
  doctor: {
    label: 'Specialist doctors',
    listSpecialtiesPath: 'doctors/specialties',
    searchLabel: 'Find specialists',
    resultsLabel: 'Matching specialists',
    ctaLabel: 'View available specialists',
    tzNote: 'Slots are saved as UTC; times are shown in each user\'s selected timezone.',
  },
  education: {
    label: 'Educators & tutors',
    listSpecialtiesPath: 'education/specialties',
    searchLabel: 'Find educators',
    resultsLabel: 'Matching educators',
    ctaLabel: 'View available educators',
    tzNote: 'Sessions run virtually unless noted; all times shown in your timezone.',
  },
  property: {
    label: 'Property, Design & Building',
    listSpecialtiesPath: 'property/specialties',
    searchLabel: 'Find property, design & building specialists',
    resultsLabel: 'Matching property, design & building specialists',
    ctaLabel: 'View available property specialists',
    tzNote: 'Consults typically run virtually unless noted; times shown in your timezone.',
  },
  tech: {
    label: 'Tech, Creative & Media',
    listSpecialtiesPath: 'tech/specialties',
    searchLabel: 'Find tech & creative specialists',
    resultsLabel: 'Matching tech & creative specialists',
    ctaLabel: 'View available tech & creative specialists',
    tzNote: 'Consults typically run virtually unless noted; times shown in your timezone.',
  },
  financial: {
    label: 'Financial advisors',
    listSpecialtiesPath: 'financial/specialties',
    searchLabel: 'Find financial advisors',
    resultsLabel: 'Matching financial advisors',
    ctaLabel: 'View available advisors',
    tzNote: 'Consults typically run virtually unless noted; times shown in your timezone.',
  },
  allied: {
    label: 'Allied Health specialists',
    listSpecialtiesPath: 'allied/specialties',
    searchLabel: 'Find Allied Health providers',
    resultsLabel: 'Matching Allied Health providers',
    ctaLabel: 'View available Allied Health providers',
    tzNote: 'Appointments typically run virtually unless noted; times shown in your timezone.',
  },
  migration: {
    label: 'Migration & Visa specialists',
    listSpecialtiesPath: 'migration/specialties',
    searchLabel: 'Find migration & visa specialists',
    resultsLabel: 'Matching migration & visa specialists',
    ctaLabel: 'View available migration specialists',
    tzNote: 'Consults typically run virtually unless noted; times shown in your timezone.',
  },
  legal: {
    label: 'Legal, Governance & Compliance',
    listSpecialtiesPath: 'legal/specialties',
    searchLabel: 'Find legal & document specialists',
    resultsLabel: 'Matching legal & document specialists',
    ctaLabel: 'View available legal professionals',
    tzNote: 'Consults typically run virtually unless noted; times shown in your timezone.',
  },
}

export const DEFAULT_PROFESSION: ProfessionKey = 'doctor'

const PROFESSION_SLUG_MAP: Record<string, ProfessionKey> = {
  doctor: 'doctor',
  doctors: 'doctor',
  education: 'education',
  educators: 'education',
  tutor: 'education',
  tutors: 'education',
  property: 'property',
  tech: 'tech',
  creative: 'tech',
  creatives: 'tech',
  'tech-creative': 'tech',
  'tech-creative-design': 'tech',
  design: 'tech',
  properties: 'property',
  realestate: 'property',
  'real-estate': 'property',
  realtor: 'property',
  realtorlicense: 'property',
  agent: 'property',
  agents: 'property',
  broker: 'property',
  brokers: 'property',
  financial: 'financial',
  finance: 'financial',
  advisor: 'financial',
  advisors: 'financial',
  adviser: 'financial',
  advisers: 'financial',
  allied: 'allied',
  'allied-health': 'allied',
  alliedhealth: 'allied',
  physio: 'allied',
  physios: 'allied',
  migration: 'migration',
  'migration-visa': 'migration',
  visa: 'migration',
  visas: 'migration',
  legal: 'legal',
  lawyer: 'legal',
  lawyers: 'legal',
  'legal-document-services': 'legal',
  'legal-documents': 'legal',
  'legal-services': 'legal',
}

const PROFESSION_CANONICAL_SLUG: Record<ProfessionKey, string> = {
  doctor: 'doctors',
  education: 'education',
  property: 'property',
  tech: 'tech',
  financial: 'financial',
  allied: 'allied',
  migration: 'migration',
  legal: 'legal',
}

export function resolveProfessionParam(raw?: string | null): ProfessionKey {
  if (!raw) return DEFAULT_PROFESSION
  const key = PROFESSION_SLUG_MAP[String(raw).toLowerCase()]
  return key || DEFAULT_PROFESSION
}

export function professionSlug(profession: ProfessionKey): string {
  return PROFESSION_CANONICAL_SLUG[profession] || profession
}

export function matchPath(profession: ProfessionKey): string {
  return `/match/${professionSlug(profession)}`
}

export function resultsPath(profession: ProfessionKey): string {
  return `/results/${professionSlug(profession)}`
}
